import { useCallback, useEffect, useRef, useState } from "react";
import { Loader2 } from "lucide-react";
import { api, USER_ID } from "../api";
import { toast } from "sonner";
import { badgeToast, melaToast, MelaCoin } from "./BadgesSheet";
import { coinRain } from "./coinRain";
import { marbleClack, flickThwip, knockPing, cheerArp } from "./sfx";

const W = 460, H = 420, CX = 230, CY = 185, R = 152;
const COLORS = ["#C0392B", "#2E86C1", "#27825C", "#E67E22", "#8E44AD", "#F0C742", "#16A085"];
const POP_WORDS = ["ਵਾਹ!", "ਸ਼ਾਬਾਸ਼!", "NICE!", "ਕਮਾਲ!", "SOLID!", "ਚੱਕ ਦੇ!", "CHAMP!"];
const HOWTO_KEY = "xp_howto_kanche";

export const KancheChampion = ({ refresh }) => {
  const canvasRef = useRef(null);
  const g = useRef(null);
  const rafRef = useRef(0);
  const [howto, setHowto] = useState(() => localStorage.getItem(HOWTO_KEY) !== "1");
  const [phase, setPhase] = useState("idle");
  const [shotsLeft, setShotsLeft] = useState(5);
  const [knocked, setKnocked] = useState(0);
  const [outColors, setOutColors] = useState([]);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => () => cancelAnimationFrame(rafRef.current), []);

  const draw = useCallback(() => {
    const cv = canvasRef.current;
    if (!cv) return;
    const ctx = cv.getContext("2d");
    const st = g.current || {};
    ctx.clearRect(0, 0, W, H);
    ctx.fillStyle = "#140E06";
    ctx.fillRect(0, 0, W, H);
    ctx.save();
    if (st.shake > 0.3) ctx.translate((Math.random() - 0.5) * st.shake, (Math.random() - 0.5) * st.shake);
    const sand = ctx.createRadialGradient(CX, CY, 20, CX, CY, R + 14);
    sand.addColorStop(0, "#C9A96A");
    sand.addColorStop(1, "#8F7440");
    ctx.fillStyle = sand;
    ctx.beginPath(); ctx.arc(CX, CY, R + 10, 0, Math.PI * 2); ctx.fill();
    ctx.strokeStyle = "#FFF6E0";
    ctx.lineWidth = 3;
    ctx.setLineDash([9, 7]);
    ctx.beginPath(); ctx.arc(CX, CY, R, 0, Math.PI * 2); ctx.stroke();
    ctx.setLineDash([]);
    ctx.lineWidth = 1;
    (st.trail || []).forEach((p) => {
      ctx.globalAlpha = p.life * 0.22;
      ctx.fillStyle = p.color;
      ctx.beginPath(); ctx.arc(p.x, p.y, p.r * p.life, 0, Math.PI * 2); ctx.fill();
    });
    ctx.globalAlpha = 1;
    const balls = [...(st.marbles || []).filter((m) => !m.out), ...(st.shooter ? [st.shooter] : [])];
    balls.forEach((m) => {
      ctx.fillStyle = "rgba(0,0,0,0.28)";
      ctx.beginPath(); ctx.ellipse(m.x + 2, m.y + m.r * 0.75, m.r * 0.9, m.r * 0.42, 0, 0, Math.PI * 2); ctx.fill();
      const gr = ctx.createRadialGradient(m.x - m.r * 0.35, m.y - m.r * 0.4, m.r * 0.15, m.x, m.y, m.r);
      gr.addColorStop(0, "#ffffffdd");
      gr.addColorStop(0.35, m.color);
      gr.addColorStop(1, "#00000088");
      ctx.fillStyle = gr;
      ctx.beginPath(); ctx.arc(m.x, m.y, m.r, 0, Math.PI * 2); ctx.fill();
      ctx.fillStyle = "rgba(255,255,255,0.65)";
      ctx.beginPath(); ctx.arc(m.x - m.r * 0.3, m.y - m.r * 0.38, m.r * 0.16, 0, Math.PI * 2); ctx.fill();
    });
    (st.flashes || []).forEach((f) => {
      ctx.globalAlpha = f.life;
      ctx.strokeStyle = "#F0C742";
      ctx.lineWidth = 3;
      ctx.beginPath(); ctx.arc(f.x, f.y, f.r * (1.6 - f.life * 0.6), 0, Math.PI * 2); ctx.stroke();
      ctx.lineWidth = 1;
    });
    (st.particles || []).forEach((p) => {
      ctx.globalAlpha = p.life;
      ctx.fillStyle = p.color;
      ctx.beginPath(); ctx.arc(p.x, p.y, 2.4 * p.life + 0.6, 0, Math.PI * 2); ctx.fill();
    });
    ctx.globalAlpha = 1;
    (st.pops || []).forEach((p) => {
      ctx.globalAlpha = Math.min(1, p.life * 1.4);
      ctx.font = p.big ? "900 24px Outfit, sans-serif" : "900 17px Outfit, sans-serif";
      ctx.textAlign = "center";
      if (p.big) { ctx.shadowColor = "#000"; ctx.shadowBlur = 8; }
      ctx.fillStyle = p.color;
      ctx.fillText(p.text, p.x, p.y - (1 - Math.min(1, p.life)) * 34);
      ctx.shadowBlur = 0;
    });
    ctx.globalAlpha = 1;
    if (st.aiming && st.drag && st.shooter) {
      const pow = Math.min(22, Math.hypot(st.drag.dx, st.drag.dy) / 5);
      ctx.strokeStyle = pow > 15 ? "rgba(226,88,34,0.85)" : "rgba(240,199,66,0.7)";
      ctx.lineWidth = 1 + pow * 0.14;
      ctx.setLineDash([4, 4]);
      ctx.beginPath();
      ctx.moveTo(st.shooter.x, st.shooter.y);
      ctx.lineTo(st.shooter.x - st.drag.dx * 1.8, st.shooter.y - st.drag.dy * 1.8);
      ctx.stroke();
      ctx.setLineDash([]);
      ctx.lineWidth = 1;
    }
    ctx.restore();
  }, []);

  const finish = async () => {
    const st = g.current;
    if (!st || st.finished) return;
    st.finished = true;
    setPhase("done");
    if (st.knocked >= 7) { cheerArp(); coinRain(44); }
    try {
      const r = await api.post("/arcade/kanche/submit", { user_id: USER_ID, session_id: st.session, knocked: st.knocked, combos: st.combos });
      setResult(r.data);
      if (r.data.mela_result?.awarded) melaToast(r.data.mela_result.message);
      badgeToast(r.data.new_badges);
      refresh();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save the round"); }
  };

  const step = useCallback(() => {
    const st = g.current;
    if (!st) return;
    let moving = false;
    const balls = [...st.marbles.filter((m) => !m.out), st.shooter];
    balls.forEach((m) => {
      m.x += m.vx; m.y += m.vy;
      m.vx *= 0.982; m.vy *= 0.982;
      const speed = Math.abs(m.vx) + Math.abs(m.vy);
      if (speed > 0.08) moving = true; else { m.vx = 0; m.vy = 0; }
      if (speed > 3) st.trail.push({ x: m.x, y: m.y, r: m.r * 0.8, color: m.color, life: 1 });
      if (m.x < m.r) { m.x = m.r; m.vx *= -0.6; }
      if (m.x > W - m.r) { m.x = W - m.r; m.vx *= -0.6; }
      if (m.y < m.r) { m.y = m.r; m.vy *= -0.6; }
      if (m.y > H - m.r) { m.y = H - m.r; m.vy *= -0.6; }
    });
    for (let i = 0; i < balls.length; i++) {
      for (let j = i + 1; j < balls.length; j++) {
        const a = balls[i], b = balls[j];
        const dx = b.x - a.x, dy = b.y - a.y, d = Math.hypot(dx, dy), min = a.r + b.r;
        if (d > 0 && d < min) {
          const nx = dx / d, ny = dy / d, push = (min - d) / 2;
          a.x -= nx * push; a.y -= ny * push;
          b.x += nx * push; b.y += ny * push;
          const av = a.vx * nx + a.vy * ny, bv = b.vx * nx + b.vy * ny;
          const impact = Math.abs(av - bv);
          a.vx += (bv - av) * nx; a.vy += (bv - av) * ny;
          b.vx += (av - bv) * nx; b.vy += (av - bv) * ny;
          const now = performance.now();
          if (impact > 1.1 && now - st.lastClack > 65) {
            st.lastClack = now;
            marbleClack(Math.min(0.13, 0.03 + impact * 0.012));
            st.shake = Math.min(7, st.shake + impact * 0.55);
          }
        }
      }
    }
    st.marbles.forEach((m) => {
      if (!m.out && Math.hypot(m.x - CX, m.y - CY) > R + 4) {
        m.out = true;
        st.knocked += 1;
        st.outOrder.push(m.color);
        setKnocked(st.knocked);
        setOutColors([...st.outOrder]);
        knockPing();
        st.shake = Math.min(9, st.shake + 4);
        st.flashes.push({ x: m.x, y: m.y, r: m.r + 6, life: 1 });
        for (let k = 0; k < 14; k++) {
          const a = Math.random() * Math.PI * 2, sp = 1 + Math.random() * 3.4;
          st.particles.push({ x: m.x, y: m.y, vx: Math.cos(a) * sp, vy: Math.sin(a) * sp, color: k % 3 === 2 ? "#F0C742" : m.color, life: 1 });
        }
        st.pops.push({ x: Math.min(W - 50, Math.max(50, m.x)), y: Math.max(40, m.y - 14), text: POP_WORDS[(st.knocked - 1) % POP_WORDS.length], color: "#F0C742", life: 1 });
        st.flickKnocked += 1;
        if (st.flickKnocked >= 2) {
          if (st.flickKnocked === 2) st.combos.push(2);
          else st.combos[st.combos.length - 1] = st.flickKnocked;
          cheerArp();
          st.shake = 10;
          st.pops.push({ x: CX, y: CY - 46, text: `REGAL RUPEES! ×${st.flickKnocked}`, color: "#F0C742", life: 1.35, big: true });
          for (let k = 0; k < 26; k++) {
            const a = Math.random() * Math.PI * 2, sp = 1.5 + Math.random() * 4;
            st.particles.push({ x: CX, y: CY, vx: Math.cos(a) * sp, vy: Math.sin(a) * sp, color: k % 2 ? "#F0C742" : "#FFF6E0", life: 1 });
          }
        }
      }
    });
    st.particles.forEach((p) => { p.x += p.vx; p.y += p.vy; p.vx *= 0.94; p.vy *= 0.94; p.life -= 0.03; });
    st.particles = st.particles.filter((p) => p.life > 0);
    st.flashes.forEach((f) => { f.life -= 0.045; });
    st.flashes = st.flashes.filter((f) => f.life > 0);
    st.pops.forEach((p) => { p.life -= 0.022; });
    st.pops = st.pops.filter((p) => p.life > 0);
    st.trail.forEach((p) => { p.life -= 0.09; });
    st.trail = st.trail.filter((p) => p.life > 0);
    st.shake *= 0.86;
    draw();
    if (!moving && !st.settled) {
      st.settled = true;
      st.shooter.x = CX; st.shooter.y = H - 34; st.shooter.vx = 0; st.shooter.vy = 0;
      if (st.shots >= 5 || st.knocked >= 7) finish();
    }
    if (moving) st.settled = false;
    const fxAlive = st.particles.length || st.flashes.length || st.pops.length || st.trail.length || st.shake > 0.3;
    if (moving || fxAlive) rafRef.current = requestAnimationFrame(step);
    else draw();
  }, [draw]); // eslint-disable-line react-hooks/exhaustive-deps

  const start = async () => {
    setBusy(true);
    setResult(null);
    try {
      const r = await api.post("/arcade/session", { user_id: USER_ID, game: "kanche" });
      const marbles = COLORS.map((color, i) => {
        const ang = (i / 7) * Math.PI * 2;
        const rad = i === 0 ? 0 : 34;
        return { x: CX + Math.cos(ang) * rad, y: CY + Math.sin(ang) * rad, vx: 0, vy: 0, r: 11, color, out: false };
      });
      g.current = { session: r.data.session_id, marbles, shooter: { x: CX, y: H - 34, vx: 0, vy: 0, r: 13, color: "#EDE3CB" }, shots: 0, knocked: 0, aiming: false, drag: null, particles: [], flashes: [], pops: [], trail: [], outOrder: [], shake: 0, lastClack: 0, settled: true, flickKnocked: 0, combos: [] };
      setShotsLeft(5); setKnocked(0); setOutColors([]); setPhase("playing");
      setTimeout(() => { draw(); canvasRef.current?.scrollIntoView({ behavior: "smooth", block: "center" }); }, 80);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not start"); }
    setBusy(false);
  };

  const ptr = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    return { x: ((e.clientX - rect.left) / rect.width) * W, y: ((e.clientY - rect.top) / rect.height) * H };
  };
  const onDown = (e) => {
    const st = g.current;
    if (!st || st.shots >= 5 || st.finished) return;
    const balls = [...st.marbles.filter((m) => !m.out), st.shooter];
    if (balls.some((m) => Math.abs(m.vx) + Math.abs(m.vy) > 0.08)) return;
    e.preventDefault();
    try { e.target.setPointerCapture(e.pointerId); } catch { /* capture unsupported */ }
    st.aiming = true;
    st.start = ptr(e);
    st.drag = { dx: 0, dy: 0 };
  };
  const onMove = (e) => {
    const st = g.current;
    if (!st?.aiming) return;
    const p = ptr(e);
    st.drag = { dx: p.x - st.start.x, dy: p.y - st.start.y };
    draw();
  };
  const onUp = () => {
    const st = g.current;
    if (!st?.aiming) return;
    st.aiming = false;
    const { dx, dy } = st.drag || { dx: 0, dy: 0 };
    st.drag = null;
    const power = Math.min(22, Math.hypot(dx, dy) / 5);
    if (power < 2) { draw(); return; }
    flickThwip();
    st.flickKnocked = 0;
    const ang = Math.atan2(-dy, -dx);
    st.shooter.vx = Math.cos(ang) * power;
    st.shooter.vy = Math.sin(ang) * power;
    st.shots += 1;
    st.settled = false;
    setShotsLeft(5 - st.shots);
    cancelAnimationFrame(rafRef.current);
    rafRef.current = requestAnimationFrame(step);
  };

  const onLeave = () => {
    const st = g.current;
    if (!st?.aiming) return;
    st.aiming = false;
    st.drag = null;
    draw();
  };

  return (
    <div data-testid="game-kanche" className="rounded-2xl bg-[#121212] border border-[#4FA3D1]/40 p-5">
      <p className="text-[10px] tracking-[0.3em] text-[#4FA3D1] font-bold">KANCHE CHAMPION · ਕੰਚੇ</p>
      <p className="text-xs text-white/50 mt-1">The street-corner marble circle — flick the shooter, knock the glass kanche out of the ring.</p>

      {howto && (
        <div data-testid="kanche-howto" className="mt-4 rounded-xl border border-[#D4AF37]/30 bg-[#1A1208] p-4">
          <p className="text-[9px] tracking-[0.25em] text-[#D4AF37] font-black">HOW TO PLAY</p>
          <p className="text-xs text-white/70 mt-1.5 leading-relaxed">Press on the shooter marble, pull back like a slingshot and release to flick it. Knock the coloured kanche OUT of the chalk circle — 5 flicks per round. Knock 2+ out with ONE flick for a REGAL RUPEES bonus, and clear all 7 for the Kanche Champ badge!</p>
          <button data-testid="kanche-howto-ok" onClick={() => { localStorage.setItem(HOWTO_KEY, "1"); setHowto(false); }}
            className="mt-3 h-9 px-5 rounded-full bg-[#D4AF37] text-black text-[10px] font-black tracking-widest">GOT IT</button>
        </div>
      )}

      {!howto && phase === "idle" && (
        <button data-testid="kanche-start" onClick={start} disabled={busy}
          className="mt-4 w-full h-12 rounded-full bg-[#4FA3D1] text-black font-black text-sm tracking-widest disabled:opacity-50">
          {busy ? <Loader2 size={15} className="animate-spin mx-auto" /> : "DRAW THE CIRCLE"}
        </button>
      )}

      {phase !== "idle" && (
        <>
          <div className="flex flex-wrap items-center justify-between mt-4 text-xs font-black gap-y-1.5">
            <span data-testid="kanche-shots" className="text-white/60">FLICKS LEFT: {shotsLeft}</span>
            <span data-testid="kanche-knocked" className="text-[#F0C742]">KNOCKED OUT: {knocked}/7</span>
            <span data-testid="kanche-trophies" className="w-full order-last flex items-center justify-center gap-1 sm:w-auto sm:order-none">
              {COLORS.map((c, i) => (
                <span key={c} className="w-3 h-3 rounded-full border transition-all duration-300"
                  style={outColors.includes(c) ? { background: c, borderColor: "#F0C742", boxShadow: `0 0 6px ${c}` } : { background: "transparent", borderColor: "rgba(255,255,255,0.2)" }} />
              ))}
            </span>
          </div>
          <div className="mt-2 rounded-xl overflow-hidden border border-white/10 select-none max-w-[500px] mx-auto">
            <canvas ref={(cv) => { canvasRef.current = cv; if (cv && phase === "playing") draw(); }}
              width={W} height={H} data-testid="kanche-canvas" className="w-full block touch-none cursor-crosshair"
              onPointerDown={onDown} onPointerMove={onMove} onPointerUp={onUp} onPointerLeave={onLeave} />
          </div>
        </>
      )}

      {phase === "done" && result && (
        <div data-testid="kanche-result" className="mt-3 rounded-xl border border-[#D4AF37]/40 bg-[#1C160A] p-4 text-center">
          {result.knocked >= 7 && <p className="text-[10px] tracking-[0.3em] text-[#F0C742] font-black animate-pulse">ਕਮਾਲ! PERFECT CLEAR!</p>}
          <p className="font-display text-2xl font-bold">{result.knocked}/7 kanche knocked out</p>
          {result.regal_bonus > 0 && <p data-testid="kanche-regal-bonus" className="text-[11px] text-[#F0C742] font-black mt-0.5 flex items-center justify-center gap-1.5"><MelaCoin size={15} /> ਸ਼ਾਹੀ! REGAL RUPEES TRICK BONUS +{result.regal_bonus}</p>}
          {result.knocked >= 4 && result.knocked < 7 && <p className="text-[11px] text-[#4FA3D1] font-bold mt-0.5">Sharp shooting — the gali is watching!</p>}
          {result.mela_result && <p className="text-xs text-[#F0C742] font-bold mt-1 flex items-center justify-center gap-1.5"><MelaCoin size={15} /> {result.mela_result.message}</p>}
          <button data-testid="kanche-again" onClick={start} className="mt-3 h-10 px-6 rounded-full border border-[#4FA3D1]/60 text-[#4FA3D1] text-[10px] font-black tracking-widest">PLAY AGAIN</button>
        </div>
      )}
    </div>
  );
};
